/**
 * Predição e reconciliação do seu atleta na partida online (item 6.2).
 *
 * O cliente não espera o servidor para ver o próprio atleta se mexer: cada comando já avança uma cópia local do
 * atleta com as mesmas funções da simulação (voo, habilidade, colisão com chão, teto, borda, postes e aros). Quando
 * chega uma foto, ela diz até qual comando o servidor já aplicou. O cliente parte do estado do servidor e
 * reaplica só os comandos que ainda não foram confirmados. Se o servidor discordar (um choque, um Petardo, um
 * pênalti), a correção aparece aí.
 *
 * Fica de fora da predição o que depende dos outros: choques entre atletas, bolas e regras. Isso vem do servidor.
 */
import { afterCollisions, startAbilities } from "./abilities.js";
import { resolveCollisions } from "./collision.js";
import { flyAthlete, type AthleteState } from "./flight.js";
import { BUTTON, NO_INPUT, type InputCommand } from "./input.js";
import { TICK_SECONDS } from "./world.js";

/** Comandos guardados sem confirmação, no máximo (uns 2 s a 30 ticks/s). */
export const MAX_PENDING = 60;

function clone(a: AthleteState): AthleteState {
  return {
    ...a,
    position: { ...a.position },
    velocity: { ...a.velocity },
    heading: { ...a.heading },
    stats: { ...a.stats },
  };
}

/** Um tick do seu atleta, sem os outros: o mesmo caminho do servidor (habilidade, voo, colisões, botões). */
export function stepOwn(prev: AthleteState, cmd: InputCommand): AthleteState {
  const before = clone(prev);
  startAbilities([before], { [before.id]: cmd });
  const a = flyAthlete(before, cmd, TICK_SECONDS);
  const events = resolveCollisions([a]);
  afterCollisions([a], events);
  // Como no fim de `stepBalls`: carga e botões para o próximo tick.
  const now = a.stunTicks > 0 ? 0 : cmd.buttons;
  a.chargeTicks = now & BUTTON.charge ? a.chargeTicks + 1 : 0;
  a.buttons = now;
  return a;
}

export interface Reconciliation {
  /** Distância entre o que o cliente tinha previsto e o que ficou depois de partir do servidor (m). */
  error: number;
  /** O mesmo, como vetor (previsto antes − previsto agora), para o cliente desfazer a correção aos poucos. */
  delta: { x: number; y: number; z: number };
  /** Comandos ainda sem confirmação, reaplicados. */
  replayed: number;
}

export class Predictor {
  private seq = 0;
  private pending: { seq: number; cmd: InputCommand }[] = [];
  /** Estado previsto agora (null antes da primeira foto). */
  predicted: AthleteState | null = null;
  /** Estado previsto no tick anterior, para desenhar entre os dois. */
  previous: AthleteState | null = null;

  constructor(readonly id: string) {}

  get unacknowledged(): number {
    return this.pending.length;
  }

  /**
   * Registra o comando do tick, avança a predição e devolve o comando numerado para mandar ao servidor.
   * `frozen`: a regra segura todo mundo agora (contagem, gol, fim); o servidor vai ignorar o comando, então a
   * predição também fica parada.
   */
  input(cmd: InputCommand, frozen = false): InputCommand & { seq: number } {
    this.seq += 1;
    const applied = frozen ? NO_INPUT : cmd;
    this.pending.push({ seq: this.seq, cmd: applied });
    if (this.pending.length > MAX_PENDING) this.pending.shift();
    if (this.predicted) {
      this.previous = this.predicted;
      this.predicted = stepOwn(this.predicted, applied);
    }
    return { ...cmd, seq: this.seq };
  }

  /**
   * Chegou uma foto: `server` é o seu atleta nela, depois do comando `ack`. Descarta os comandos confirmados e
   * reaplica os outros por cima do estado do servidor.
   */
  reconcile(server: AthleteState, ack: number): Reconciliation {
    this.pending = this.pending.filter((p) => p.seq > ack);
    let s = clone(server);
    for (const p of this.pending) s = stepOwn(s, p.cmd);
    const old = this.predicted;
    const dx = old ? old.position.x - s.position.x : 0;
    const dy = old ? old.position.y - s.position.y : 0;
    const dz = old ? old.position.z - s.position.z : 0;
    const error = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (!old) this.previous = s;
    this.predicted = s;
    return { error, delta: { x: dx, y: dy, z: dz }, replayed: this.pending.length };
  }
}
